import { type ThreadId, type TurnId } from "@cmmd-forge/contracts";
import type { ComposerDisabledReason } from "./composerTurnPlan";
import { buildChatComposerRuntimeInput } from "./buildChatComposerRuntimeProps";
import { type LoadedSurfaceInput } from "./buildChatLoadedThreadSurfaceProps";
import { type useChatRouteRuntime } from "./useChatRouteRuntime";
import { type useChatThreadActionsRuntime } from "./useChatThreadActionsRuntime";
import { type useChatThreadControlsRuntime } from "./useChatThreadControlsRuntime";
import { type useChatThreadRuntimeState } from "./useChatThreadRuntimeState";
import { type usePlanApprovalControls } from "./usePlanApprovalControls";
import type {
  ForgeProviderAccessState,
  ProviderCredentialMode,
} from "../../cmmd/forgeProviderAccess";
import type { ModelPickerProviderAvailability } from "./modelPickerProviderAvailability";

export interface ChatLoadedThreadRuntimeInputOptions {
  readonly showRightPanelLauncher: boolean;
}

/** Assembles loaded-thread view inputs and queue actions; throws without an active thread. */
export function buildChatLoadedThreadRuntimeInput(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly threadRuntime: ReturnType<typeof useChatThreadRuntimeState>;
  readonly controlsRuntime: ReturnType<typeof useChatThreadControlsRuntime>;
  readonly actions: ReturnType<typeof useChatThreadActionsRuntime>;
  readonly planApproval: ReturnType<typeof usePlanApprovalControls>;
  readonly providerState: {
    readonly selectedProvider: LoadedSurfaceInput["composerProps"]["resolvedProvider"];
    readonly lockedProvider: LoadedSurfaceInput["composerProps"]["lockedProvider"];
    readonly composerDisabledReason: ComposerDisabledReason;
    readonly providerAccess: ForgeProviderAccessState;
    readonly providerCredentialMode: ProviderCredentialMode;
    readonly providerAvailability: ReadonlyArray<ModelPickerProviderAvailability>;
  };
  readonly activeProviderStatus: LoadedSurfaceInput["activeProviderStatus"];
  readonly usageNotice: LoadedSurfaceInput["usageNotice"];
  readonly onOpenUsageLimits: () => boolean;
  readonly options: ChatLoadedThreadRuntimeInputOptions;
}): LoadedSurfaceInput {
  const { activeThread } = input.routeRuntime.bootstrap;
  if (!activeThread) {
    throw new Error("Cannot build loaded chat input without an active thread");
  }

  return {
    activeThread,
    activeProviderStatus: input.activeProviderStatus,
    usageNotice: input.usageNotice,
    queuedMessages: input.actions.queuedMessages,
    onSteerQueuedMessage: input.actions.onSteerQueuedMessage,
    onRemoveQueuedMessage: input.actions.onRemoveQueuedMessage,
    ...buildHeaderInput(input),
    ...buildTimelineInput(input),
    ...buildPrepInput(input),
    ...buildApprovalInput(input),
    ...buildChatComposerRuntimeInput(input),
    ...buildOverlayInput(input),
  };
}

// This inherited header builder only receives the extended provider-state input.
// fallow-ignore-next-line complexity
function buildHeaderInput(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly threadRuntime: ReturnType<typeof useChatThreadRuntimeState>;
  readonly controlsRuntime: ReturnType<typeof useChatThreadControlsRuntime>;
  readonly options: ChatLoadedThreadRuntimeInputOptions;
}) {
  const { route, bootstrap } = input.routeRuntime;
  const { projectContext } = input.threadRuntime;
  const { workspace, shortcuts, panels, terminal, env } = input.controlsRuntime;
  return {
    activeProject: projectContext.activeProject,
    onRunScript: terminal.runProjectScript,
    setThreadError: bootstrap.setThreadError,
    activeProjectCwd: workspace.activeProjectCwd,
    activeThreadWorktreePath: workspace.activeThreadWorktreePath,
    activeThreadBranch: env.activeThreadBranch,
    activeThreadEnvMode: env.envMode,
    gitBranch: workspace.gitStatusQuery.data?.branch ?? null,
    // The thread's own link answers for the chip when git status has not seen the branch's pull
    // request: the number and URL are durable on the link, the state is not, and the composer
    // renders that honestly as "pending".
    gitPrState:
      workspace.gitStatusQuery.data?.pr?.state ??
      (bootstrap.activeThread?.linkedPullRequest ? ("pending" as const) : null),
    gitPrNumber:
      workspace.gitStatusQuery.data?.pr?.number ??
      bootstrap.activeThread?.linkedPullRequest?.number ??
      null,
    gitPrUrl:
      workspace.gitStatusQuery.data?.pr?.url ??
      bootstrap.activeThread?.linkedPullRequest?.url ??
      null,
    gitPrTitle: workspace.gitStatusQuery.data?.pr?.title ?? null,
    activeThreadParentTitle: projectContext.activeThreadTeam?.master?.title ?? null,
    activeThreadWorkerCount: projectContext.activeThreadTeam?.workers.length ?? 0,
    jumpToParentShortcutLabel: shortcuts.labels.jumpToParentShortcutLabel,
    onJumpToParent: panels.onJumpToParent,
    isServerThread: bootstrap.isServerThread,
    canOpenRightSidebar: bootstrap.canOpenRightSidebar,
    previewOpen: panels.previewOpen,
    canvasOpen: panels.canvasOpen,
    diffOpen: panels.diffOpen,
    planOpen: panels.planOpen,
    terminalState: bootstrap.terminalState,
    onTogglePreview: panels.onTogglePreview,
    onToggleCanvasInvert: panels.onToggleCanvasInvert,
    onToggleDiff: panels.onToggleDiff,
    togglePlanSidebar: panels.togglePlanSidebar,
    toggleTerminalVisibility: terminal.toggleTerminalVisibility,
    previewPanelShortcutLabel: shortcuts.labels.previewPanelShortcutLabel,
    canvasPanelShortcutLabel: shortcuts.labels.canvasPanelShortcutLabel,
    diffPanelShortcutLabel: shortcuts.labels.diffPanelShortcutLabel,
    planPanelShortcutLabel: shortcuts.labels.planPanelShortcutLabel,
    terminalToggleShortcutLabel: shortcuts.labels.terminalToggleShortcutLabel,
    showRightPanelLauncher: input.options.showRightPanelLauncher,
    gitCwd: workspace.gitCwd,
    activeThreadRef: bootstrap.activeThreadRef,
    routeKind: route.routeKind,
    draftId: route.draftId,
  };
}

// fallow-ignore-next-line complexity -- port of forge builder (pre-port) at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
function buildTimelineInput(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly threadRuntime: ReturnType<typeof useChatThreadRuntimeState>;
  readonly controlsRuntime: ReturnType<typeof useChatThreadControlsRuntime>;
  readonly actions: ReturnType<typeof useChatThreadActionsRuntime>;
}) {
  const { route, bootstrap, imagePreview, runtimeFlags, settings, theme } = input.routeRuntime;
  const { projectContext, activity, work, timeline } = input.threadRuntime;
  const { panels, scroll } = input.controlsRuntime;
  return {
    timelineProps: {
      isWorking: work.isWorking,
      activeTurnInProgress: work.isWorking || !projectContext.latestTurnSettled,
      activeTurnId: projectContext.activeLatestTurn?.turnId ?? null,
      activeTurnStartedAt: work.activeWorkStartedAt,
      latestTurn: projectContext.activeLatestTurn ?? null,
      runningTurnId:
        projectContext.activeLatestTurn?.state === "running"
          ? projectContext.activeLatestTurn.turnId
          : null,
      listRef: scroll.legendListRef,
      timelineEntries: timeline.timelineEntries,
      turnDiffSummaryByAssistantMessageId: timeline.turnDiffSummaryByAssistantMessageId,
      routeThreadKey: bootstrap.routeThreadKey,
      onOpenTurnDiff: panels.onOpenTurnDiff,
      revertTurnCountByUserMessageId: timeline.revertTurnCountByUserMessageId,
      onRevertUserMessage: input.actions.onRevertUserMessage,
      onApproveGitHubConfirmation: input.actions.onApproveGitHubConfirmation,
      onRetryGitHubActionRequired: input.actions.onRetryGitHubActionRequired,
      activeActionRequired: activity.activeActionRequired,
      suppressedGitHubConfirmationId: input.actions.isGitHubApprovalOverlayVisible
        ? (timeline.activePendingGitHubConfirmation?.confirmationId ?? null)
        : null,
      declinedGitHubConfirmationId: input.actions.declinedGitHubConfirmationId,
      isRevertingCheckpoint: runtimeFlags.isRevertingCheckpoint,
      onImageExpand: imagePreview.onExpandTimelineImage,
      resolvedTheme: theme.resolvedTheme,
      timestampFormat: settings.timestampFormat,
      onIsAtEndChange: scroll.onIsAtEndChange,
      runtimeAdmission: input.actions.runtimeAdmission,
    },
    showScrollToBottom: scroll.showScrollToBottom,
    scrollToEnd: scroll.scrollToEnd,
    environmentId: route.environmentId,
    pendingAutoCreatePrompt: bootstrap.activeThread?.pendingAutoCreatePrompt ?? null,
    activePlan: activity.activePlan as { readonly turnId?: TurnId } | null,
  };
}

function buildPrepInput(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly threadRuntime: ReturnType<typeof useChatThreadRuntimeState>;
  readonly controlsRuntime: ReturnType<typeof useChatThreadControlsRuntime>;
  readonly planApproval: ReturnType<typeof usePlanApprovalControls>;
}) {
  const { route, bootstrap, navigate } = input.routeRuntime;
  const { projectContext, activity } = input.threadRuntime;
  const { workspace, panels } = input.controlsRuntime;
  return {
    onApprovePlan: input.planApproval.onApprovePlan,
    onEditPlan: input.planApproval.onEditPlan,
    onDiscardPlan: input.planApproval.onDiscardPlan,
    showAgentsRow: projectContext.showAgentsRow,
    activeWorkspaceRoot: workspace.activeWorkspaceRoot,
    activeThreadTeam: projectContext.activeThreadTeam,
    onOpenWorkerThread: (targetThreadId: ThreadId) => {
      void navigate({
        to: "/$environmentId/$threadId",
        params: { environmentId: route.environmentId, threadId: targetThreadId },
      });
    },
    activeTaskContextModel: workspace.activeTaskContextModel,
    onViewActiveTaskContext: panels.onViewActiveTaskContext,
    serverThreadStatus: bootstrap.serverThreadStatus,
    serverThreadSubscriptionError: bootstrap.serverThreadSubscriptionError,
    activePlan: activity.activePlan as { readonly turnId?: TurnId } | null,
  };
}

function buildApprovalInput(input: {
  readonly threadRuntime: ReturnType<typeof useChatThreadRuntimeState>;
  readonly actions: ReturnType<typeof useChatThreadActionsRuntime>;
}) {
  return {
    isGitHubApprovalOverlayVisible: input.actions.isGitHubApprovalOverlayVisible,
    activePendingGitHubConfirmation: input.threadRuntime.timeline.activePendingGitHubConfirmation,
    onApproveGitHubConfirmation: input.actions.onApproveGitHubConfirmation,
    onDeclineGitHubConfirmation: input.actions.onDeclineGitHubConfirmation,
    onGitHubApprovalFeedback: input.actions.onGitHubApprovalFeedback,
    setGitHubApprovalOverlayDismissed: input.actions.setGitHubApprovalOverlayDismissed,
  };
}

function buildOverlayInput(input: {
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
  readonly controlsRuntime: ReturnType<typeof useChatThreadControlsRuntime>;
}) {
  const { bootstrap, composer, imagePreview } = input.routeRuntime;
  const { shortcuts, terminal } = input.controlsRuntime;
  return {
    mountedTerminalThreadRefs: bootstrap.mountedTerminalThreadRefs,
    activeThreadKey: bootstrap.activeThreadKey,
    activeTerminalLaunchContext: terminal.activeTerminalLaunchContext,
    terminalFocusRequestId: terminal.terminalFocusRequestId,
    splitTerminalShortcutLabel: shortcuts.labels.splitTerminalShortcutLabel,
    newTerminalShortcutLabel: shortcuts.labels.newTerminalShortcutLabel,
    closeTerminalShortcutLabel: shortcuts.labels.closeTerminalShortcutLabel,
    addTerminalContextToDraft: composer.addTerminalContextToDraft,
    expandedImage: imagePreview.expandedImage,
    closeExpandedImage: imagePreview.closeExpandedImage,
  };
}
